/**
 * InteractiveEdge - Custom edge with hover widget for edit and remove actions
 * 
 * Shows a white widget with edit and remove buttons when hovering over the edge
 */

import React, { useState, useCallback } from 'react';
import {
  EdgeProps,
  getBezierPath,
  getStraightPath,
  getSmoothStepPath,
  BaseEdge,
  EdgeLabelRenderer,
} from '@xyflow/react';
import { Pencil, Trash2 } from 'lucide-react';

interface InteractiveEdgeProps extends EdgeProps {
  sourceHandle?: string | null;
  targetHandle?: string | null;
  animated?: boolean;
}

export function InteractiveEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
  selected,
  type = 'default',
  label,
  source,
  target,
  sourceHandle,
  targetHandle,
  animated,
}: InteractiveEdgeProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Get handlers from edge data (injected by FlowCanvas)
  const onEdgeContextMenu = data?.onEdgeContextMenu;
  const onDeleteEdge = data?.onDeleteEdge;

  // Get the path based on edge type
  let edgePath: string;
  let labelX: number;
  let labelY: number;

  if (type === 'straight') {
    [edgePath, labelX, labelY] = getStraightPath({
      sourceX,
      sourceY,
      targetX,
      targetY,
    });
  } else if (type === 'step' || type === 'smoothstep') {
    [edgePath, labelX, labelY] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });
  } else {
    // default, simplebezier
    [edgePath, labelX, labelY] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });
  }

  const handleEditClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (typeof onEdgeContextMenu === "function") {
      // Create full edge object for the context menu
      const edge = {
        id,
        type: type || 'default',
        source,
        target,
        sourceHandle,
        targetHandle,
        animated,
        data,
        style,
        label,
      };
      onEdgeContextMenu(event, edge);
    }
  }, [id, type, source, target, sourceHandle, targetHandle, animated, data, style, label, onEdgeContextMenu]);

  const handleRemoveClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (typeof onDeleteEdge === "function") {
      onDeleteEdge(id);
    }
  }, [id, onDeleteEdge]);

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={style}
      />
      
      {/* Invisible wider path for easier hover detection */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={20}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{ cursor: 'pointer' }}
      />

      {/* Hover widget with edit and remove buttons */}
      {(isHovered || selected) && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY - (label ? 20 : 0)}px)`,
              pointerEvents: 'all',
            }}
            className="flex gap-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-1"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Edit button */}
            <button
              onClick={handleEditClick}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors group"
              title="Edit edge style" 
            >
              <Pencil className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
            </button>

            {/* Remove button */}
            <button
              onClick={handleRemoveClick}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors group"
              title="Remove edge"
            >
              <Trash2 className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300 group-hover:text-red-600 dark:group-hover:text-red-400" />
            </button>
          </div>
        </EdgeLabelRenderer>
      )}

      {/* Label if present - shown below the widget */}
      {label && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'none',
              fontSize: 10,
              fontWeight: 500,
            }}
            className="bg-white dark:bg-gray-800 px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300"
          >
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
